import type { ReactNode } from 'react';
import { TaskItemCard, TaskItemField, TaskItemText } from '@/features/contribution-kit';
import type { Question } from '@/types/theme.types';
import type { InitialDataCopy } from '../initial-data-copy';

type QuestionCardProps = {
  copy: InitialDataCopy;
  question: Question;
  footer?: ReactNode;
};

/** Spec 005: the question's number in the uploaded workbook, when it is a real one. */
function workbookOrder(question: Question): number | null {
  const order = question.order;
  return typeof order === 'number' && Number.isSafeInteger(order) && order > 0 ? order : null;
}

/**
 * The question on screen: the French wording, and the local one when it
 * differs. Its number in the uploaded workbook sits in the corner, so a
 * collector can find the same question in the file.
 */
export function QuestionCard({ copy, question, footer }: QuestionCardProps) {
  const french = question.textFr?.trim() || question.textLocal?.trim() || '';
  const local = question.textLocal?.trim();
  const showLocal = Boolean(local && local !== french);
  const fileOrder = workbookOrder(question);

  return (
    <TaskItemCard
      label={copy.questionLabel}
      footer={footer}
      corner={
        fileOrder !== null ? (
          <span
            className="rounded-full border border-border/60 bg-surface-2 px-2.5 py-1 font-mono text-xs tabular-nums text-muted-foreground"
            data-testid="initial-data-file-order"
          >
            {copy.questionFileOrder} {fileOrder}
          </span>
        ) : null
      }
    >
      <div data-testid="initial-data-question">
        <TaskItemText quoted={false} className="py-2 text-left sm:text-xl">
          {french || <span className="text-base font-normal text-muted-foreground">{copy.questionEmpty}</span>}
        </TaskItemText>
      </div>
      {showLocal && <TaskItemField label={copy.localVersionLabel}>{local}</TaskItemField>}
    </TaskItemCard>
  );
}
